import React, { useState, useEffect } from 'react'
import I18n from "lib/i18n/i18n"
import { useSelector, useDispatch } from "react-redux"
import moment from "moment"
import {
    LargePrimaryButton,
    DropDown,
    DropItem,
    SuccessAlert,
    PhoneInput,
    SmallLinkButton, ModalPattern
} from "@worthy-npm/clarity"
import { formatPhone, TIME_ZONE, getDateTimeDescription, imageUrl, isMobile, isFakePhone } from 'helpers/ApplicationHelper'
import { scheduleAppointment, updateMissingDetails } from "../../actions/user"
import { convertDateTimeToLocalTime } from "../../helpers/dateTimeHelper"
import PageBlocked from "./pageBlocked"
import ConfirmCancellationModal from "./confirmCancellationModal"
import ConfirmCancellationPopup from "./confirmCancellationPopup"
import CalendlyHelper from 'lib/calendly'
import { useAppointment } from "../../hooks/useAppointment"


const CallDetails = (props) => {

    const { ADO } = useSelector(state => state)
    const dispatch = useDispatch()
    const { data }  = useAppointment(ADO?.user?.id)
    const [editableTime, setEditableTime] = useState(false)
    const [editablePhone, setEditablePhone] = useState(false)
    const [slot, setSlot] = useState(null)
    const [phone, setPhone] = useState(data?.phone)
    const [showAlert, setShowAlert] = useState(false)
    const [showPhoneValidationMessage, setShowPhoneValidationMessage] = useState(false)
    const [isPhoneValid, setIsPhoneValid] = useState(true)
    const [phoneInput, setPhoneInput] = useState('')
    const [fieldUpdated, setFieldUpdated] = useState(null)
    const [openCancelModal, setOpenCancelModal] = useState(false)
    const [loadCalendly, setLoadCalendly] = useState(false)

    useEffect(() => {
        window.addEventListener('message', calendlyEvent)
    if (!props.pageBlocked() && props.pageMode === 'schedule') {
        const { first_name, last_name, email, phone, id } = ADO.user
        const calendlyParams = `?name=${first_name} ${last_name || ''}&email=${email}${phone && !isFakePhone(phone) ? `&location=1${phone}` : ''}&utm_source=${id}&utm_campaign=${data.objectId}&utm_term=${data.objectType}`

        initCalendlyWidget(`${data.schedulingUrl}${calendlyParams}`)
    }

    }, []);

    const calendlyEvent = (e) => {
        if (e.data?.event === 'calendly.event_type_viewed') {
            // Resize iframe on rescheduling
            let iFrames = document.querySelector('iframe')
            iFrames.style.minHeight = '1000px'
        }
        if(e.data.event?.indexOf('event_scheduled') > -1){
            props.logger(`Schedule call set using Calendly`)
        }
    }

    const getTimeDesc = (time_str) => {
        let time = moment(time_str)

        return getDateTimeDescription(time, true)
    }

    const isCalendlyCall = () => {
        return data?.setThrough === 'calendly'
    }

    const initCalendlyWidget = (calendlyLink) => {
        setLoadCalendly(true)
        CalendlyHelper.intiInlineWidget(calendlyLink, 'call-details-frame')
    }

    const onEditClicked = (key) => {
        props.logger(`Edit ${key} clicked`)
        setShowAlert(false)
        if (key === 'time') {
            isCalendlyCall() ? initCalendlyWidget(data.calendlyRescheduleUrl) : setEditableTime(true)
        } else {
            setEditablePhone(true)
        }
    }

    const objectTypeDataMapping = {
        'High Value': {
            callHours: 'high_value_call_hours',
            freeSlots: 'high_value_free_slots',
            chatBotType: 'highValue'
        },
        'Jewelry Box': {
            callHours: 'jb_call_hours',
            freeSlots: 'jb_free_slots',
            chatBotType: 'jewelryBox'
        }
    }

    const getTimeSlots = () => {
        const day =  moment().format('dddd').toLowerCase()
        const numbersOfDaysToShow = ADO[objectTypeDataMapping[data.objectType].callHours][day] || 2

        let options = {}
        for (let time_str of ADO[objectTypeDataMapping[data.objectType].freeSlots]) {
            let time = moment(time_str);
            if (time.diff(moment(),'days') >= numbersOfDaysToShow) {continue;}
            options[time.format()] = getTimeDesc(time_str)
        }
        return options
    }

    const setTimeSlot = (slot) => {
        setSlot(slot)
    }

    const rescheduleAppointment = (stopAnimation) => {
        const time = moment(slot);

        let preferredTimeDate = convertDateTimeToLocalTime(TIME_ZONE, time);
        let preferredDayText = getDateTimeDescription(time)
        let preferredTimeText = time.format('h:mm a');

        const details  = {
            appointmentDetails: {
                chatBotType: objectTypeDataMapping[data.objectType].chatBotType,
                phone: phone,
                preferredDayText: preferredDayText,
                preferredTimeDate: preferredTimeDate,
                preferredTimeText: preferredTimeText,
                timezoneName: moment.tz.guess(),
                version: 'manage_call_vip'
            },
            objectId: data.objectId
        }

        dispatch(scheduleAppointment(details))
            .then(() => {
                stopAnimation()
                setFieldUpdated('time')
                setShowAlert(true)
                setEditableTime(false)
                props.logger('Call rescheduled')
            })
            .catch(() => {
                stopAnimation()
                setEditableTime(false)
            })
    }

    const updatePhone = (stopAnimation) => {
        if (phone !== phoneInput) {
            setShowPhoneValidationMessage(true)

            if (isPhoneValid) {
                dispatch(updateMissingDetails({
                    phone: phoneInput,
                    user_id: ADO.user.id
                }))
                    .then(() => {
                        props.logger('Phone number updated')
                        stopAnimation()
                        setFieldUpdated('phone')
                        setShowAlert(true)
                        setPhone(phoneInput)
                        setEditablePhone(false)
                    })
                    .catch(() => {
                        stopAnimation()
                        setEditablePhone(false)
                    })
            }
        else {
            stopAnimation()
            }
        }
    }

    const alertMessage = () => {
        return ( () => {
            return (<div>
                <span className={'success-word'}>{ `${I18n.getString('manageCall::callDetails::successWord')}! ` }</span>
                <span>{ fieldUpdated === 'time' ? I18n.getString('manageCall::callDetails::rescheduleSuccessMessage') : I18n.getString('manageCall::callDetails::phoneUpdatedSuccessMessage') }</span>
            </div>
            )
        })
    }

    const onPhoneTyped = (value, isValid) => {
        setIsPhoneValid(isValid)
        setPhoneInput(value.replace(/[\D\s._-]+/g, ""))
    }

    const cancelEdit = (prop) => {
        props.logger(`Cancel Edit ${prop} clicked`)

        if(prop === 'time') {
            setEditableTime(false)
        }
        else {
            setEditablePhone(false)
        }
    }

    const confirmCancellation = () => {
        props.logger(`Cancel call link clicked`)

        if (isMobile()) {
            setOpenCancelModal(true)
        }
        else {
            ConfirmCancellationPopup.initPopup({ appointmentId: data.appointmentId, logger: props.logger })
        }
    }

    const onCloseModal = () => {
        setOpenCancelModal(false)
    }

    const confirmCancellationModal = () => {
        return (
            <ModalPattern
                key={'CompleteAccountModalPattern'}
                textAlign={'center'}
                closeIcon={ true }
                closeOnClickOutside={ false }
                closeOnEscape={ false }
                isWide={ true }
                onClose={ () => {
                    onCloseModal()
                }}>
               <ConfirmCancellationModal appointmentId={data.appointmentId} onCloseModal={onCloseModal} logger={props.logger} />
            </ModalPattern>
        )
    }


    return (
        <div id={'call-details-frame'} className={`call-details-frame ${ showAlert || loadCalendly ? 'full-height' : '' } ${ loadCalendly ? 'calendly' : ''}`}>
            {
                !loadCalendly && (
                !props.pageBlocked() ?
                    <div   className={'wrapper'}>
                        {
                            showAlert && fieldUpdated &&  <SuccessAlert TextComponent={ alertMessage() }
                                                       onClose={ () => { setShowAlert(false)}}/>
                        }
                        <div className={'title-wrap'}>
                            <div className={'title-details'}>{I18n.getString('manageCall::callDetails::title')}</div>
                            <SmallLinkButton
                                text={I18n.getString('manageCall::callDetails::cancel')}
                                click={() => { isCalendlyCall() ? initCalendlyWidget(data.calendlyCancelUrl) : confirmCancellation() }}
                                overrideCSS={{'marginRight': '-12px'}}/>
                        </div>
                            {
                                editableTime ?
                                    <div className={'edit-wrap'}>
                                        <div className={'prop-edit-wrap'}>
                                            <DropDown
                                                name="time-slot"
                                                change={ slot => setTimeSlot(slot) }
                                                placeholder={I18n.getString('manageCall::callDetails::selectTimeSlotsPlaceholder')}
                                                marginVsides='xs'
                                                overrideWrapperCSS={{'width': '100%'}}>
                                                {
                                                    Object.entries(getTimeSlots()).map(([key, value], i) => {
                                                        return <DropItem key={i} label={value} value={key}/>
                                                    })
                                                }
                                            </DropDown>
                                        </div>
                                    <img src={imageUrl('cancel.svg')} onClick={() => {cancelEdit('time')}} /> </div>
                                    :
                                    <div className={'prop-wrap'}>
                                        <div className={'prop-data'}>
                                            <div className={'key'}>{ I18n.getString('manageCall::callDetails::time') }</div>
                                            <div className={'value'}>{ getTimeDesc(slot || data && data.scheduleTime) }</div>
                                        </div>
                                        <div className={'call-edit-icon'} onClick={ () => onEditClicked('time') }></div>
                                    </div>
                            }
                            {
                                editablePhone ?
                                    <div className={'edit-wrap'}>
                                        <div className={'prop-edit-wrap'}>
                                        <PhoneInput
                                            change={(value, isValid)=>onPhoneTyped(value, isValid)}
                                            showValidationMessage={showPhoneValidationMessage}
                                            label={I18n.getString('components::scheduleCall::phone::phoneInputLabel')}
                                            overrideWrapperCSS={{'width': '100%', 'paddingTop': '22px'}}
                                            autoFocus={true}
                                        />
                                        </div>
                                        <img src={imageUrl('cancel.svg')} onClick={() => {cancelEdit('phone')}} /> </div>
                                    :
                                    <div className={'prop-wrap'}>
                                        <div  className={'prop-data'}>
                                            <div className={'key'}>{I18n.getString('manageCall::callDetails::phoneNumber')}</div>
                                            <div className={'value'}>{phone && !isFakePhone(phone) ? `1 ${data ? formatPhone(phone) : ''} ` : '-'}</div>
                                        </div>
                                        <div className={'call-edit-icon'} onClick={ () => onEditClicked('phone') }></div>
                                    </div>
                            }

                        <LargePrimaryButton text={I18n.getString('manageCall::callDetails::saveBtn')}
                                            overrideCSS={{'marginBottom': '60px', 'width': '290px', 'marginTop': '30px'}}
                                            disabled={!editableTime && !editablePhone}
                                            click={(stopAnimation) => { editableTime ? rescheduleAppointment(stopAnimation) : updatePhone(stopAnimation) }}/>
                    </div> : <PageBlocked />)
            }
            { openCancelModal && confirmCancellationModal() }
        </div>
    )
}

export default CallDetails
